import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Trash2, Plus, Loader2, AlertTriangle } from "lucide-react";

const MOTIVOS = [
  { value: "obsoleto", label: "Obsoleto" },
  { value: "danado", label: "Dañado" },
  { value: "perdido", label: "Perdido" },
  { value: "donado", label: "Donado" },
  { value: "vendido", label: "Vendido" }
];

export default function MobiliarioBajasPage() {
  const [bajas, setBajas] = useState([]);
  const [activos, setActivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ activo_id: "", motivo: "obsoleto", descripcion: "", valor_residual: 0, fecha_baja: "", documento_autorizacion: "" });
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [bajasRes, activosRes] = await Promise.all([
        axios.get(`${API}/mobiliario/bajas`, getAuthHeaders()),
        axios.get(`${API}/mobiliario/activos?estado=disponible`, getAuthHeaders())
      ]);
      setBajas(bajasRes.data || []);
      // Also get activos en mantenimiento
      const mantRes = await axios.get(`${API}/mobiliario/activos?estado=mantenimiento`, getAuthHeaders());
      setActivos([...(activosRes.data || []), ...(mantRes.data || [])]);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => { 
    setFormData({ activo_id: "", motivo: "obsoleto", descripcion: "", valor_residual: 0, fecha_baja: new Date().toISOString().split('T')[0], documento_autorizacion: "" }); 
    setShowDialog(true); 
  };

  const handleSubmit = async () => {
    if (!formData.activo_id || !formData.motivo) { toast.error("Activo y motivo requeridos"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/mobiliario/bajas`, formData, getAuthHeaders());
      toast.success("Activo dado de baja");
      setShowDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900"><Sidebar /><main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></main></div>;

  const totalValorOriginal = bajas.reduce((sum, b) => sum + (b.valor_original || 0), 0);
  const totalValorResidual = bajas.reduce((sum, b) => sum + (b.valor_residual || 0), 0);

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="mobiliario-bajas-page">
        <div className="flex justify-between mb-6">
          <div><h1 className="text-3xl font-bold dark:text-white">Bajas de Mobiliario</h1><p className="text-slate-500">{bajas.length} activos dados de baja</p></div>
          <Button onClick={openNew} className="bg-red-600 hover:bg-red-700 text-white"><Plus className="w-4 h-4 mr-2" />Nueva Baja</Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="p-4">
              <p className="text-sm text-red-600">Valor Original Total</p>
              <p className="text-2xl font-bold text-red-700">RD$ {totalValorOriginal.toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <p className="text-sm text-slate-600">Valor Residual Total</p>
              <p className="text-2xl font-bold text-slate-700">RD$ {totalValorResidual.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-slate-50 dark:bg-slate-800">
                  <th className="text-left p-4 font-medium text-slate-600">Activo</th>
                  <th className="text-left p-4 font-medium text-slate-600">Motivo</th>
                  <th className="text-left p-4 font-medium text-slate-600">Descripción</th>
                  <th className="text-left p-4 font-medium text-slate-600">Fecha Baja</th>
                  <th className="text-left p-4 font-medium text-slate-600">Valor Original</th>
                  <th className="text-left p-4 font-medium text-slate-600">Valor Residual</th>
                </tr>
              </thead>
              <tbody>
                {bajas.length === 0 ? (
                  <tr><td colSpan="6" className="p-8 text-center text-slate-500">No hay bajas registradas</td></tr>
                ) : bajas.map(baja => (
                  <tr key={baja.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="p-4"><p className="font-medium dark:text-white">{baja.activo_nombre}</p><p className="text-sm text-slate-500">{baja.activo_codigo}</p></td>
                    <td className="p-4"><Badge variant="outline" className="bg-red-100 text-red-700">{MOTIVOS.find(m => m.value === baja.motivo)?.label || baja.motivo}</Badge></td>
                    <td className="p-4 text-slate-600 text-sm max-w-xs truncate">{baja.descripcion || "-"}</td>
                    <td className="p-4 text-slate-600">{baja.fecha_baja}</td>
                    <td className="p-4 text-slate-600">RD$ {baja.valor_original?.toLocaleString() || 0}</td>
                    <td className="p-4 text-slate-600">RD$ {baja.valor_residual?.toLocaleString() || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" />Dar de Baja Activo</DialogTitle>
            </DialogHeader>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800"><strong>Advertencia:</strong> Esta acción marcará el activo como dado de baja. No podrá ser asignado ni utilizado.</p>
            </div>
            <div className="space-y-4 py-4">
              <div>
                <Label>Activo a dar de baja *</Label>
                <Select value={formData.activo_id} onValueChange={v => setFormData({...formData, activo_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar activo..." /></SelectTrigger>
                  <SelectContent>
                    {activos.map(a => <SelectItem key={a.id} value={a.id}>{a.codigo} - {a.nombre} (RD$ {a.valor_adquisicion?.toLocaleString()})</SelectItem>)}
                  </SelectContent>
                </Select>
                {activos.length === 0 && <p className="text-sm text-amber-600 mt-1">No hay activos disponibles para dar de baja</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Motivo *</Label>
                  <Select value={formData.motivo} onValueChange={v => setFormData({...formData, motivo: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{MOTIVOS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fecha de Baja</Label>
                  <Input type="date" value={formData.fecha_baja} onChange={e => setFormData({...formData, fecha_baja: e.target.value})} />
                </div>
              </div>
              <div><Label>Descripción</Label><Textarea value={formData.descripcion} onChange={e => setFormData({...formData, descripcion: e.target.value})} placeholder="Describir el motivo de la baja..." /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Valor Residual</Label><Input type="number" value={formData.valor_residual} onChange={e => setFormData({...formData, valor_residual: parseFloat(e.target.value) || 0})} /></div>
                <div><Label>Documento de Autorización</Label><Input value={formData.documento_autorizacion} onChange={e => setFormData({...formData, documento_autorizacion: e.target.value})} placeholder="Número de documento" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSubmit} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Confirmar Baja</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
